import { DialogActions, DialogProps, DialogTitle, Typography, useTheme } from "@mui/material";
import { FC, useContext } from "react";
import LotsContext from "../../contexts/data/LotsContext";
import StagesContext from "../../contexts/data/StagesContext";
import LotAttachmentsContext from "../../contexts/LotAttachmentsContext";
import LotDetailsContext from "../../contexts/LotDetailsContext";
import LotNotesContext from "../../contexts/LotNotesContext";
import LotSummariesContext from "../../contexts/LotSummariesContext";
import StageAttachmentsContext from "../../contexts/StageAttachmentsContext";
import StageDetailsContext from "../../contexts/StageDetailsContext";
import StageSummariesContext from "../../contexts/StageSummariesContext";
import { FormSection, StyledButton, StyledDialog } from "../../forms/inputs/CommonFormElements";
import useDelete from "../../hooks/data/useDelete";
import ReportsContext from "../../contexts/ReportsContext";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";
import ReportPreviewContext from "../../contexts/ReportPreviewContext";
import ReportTemplatePreviewContext from "../../contexts/ReportTemplatePreviewContext";

export enum DeleteTargetTypes {
    Project = 1,
    Stage = 2,
    Lot = 3,
    Contract = 4,
    Attachment = 5,
    LotNote = 6,
    Report = 7,
    ReportTemplate = 8,
    Asset = 9
}

interface IDeleteDialogProps extends Omit<DialogProps, 'onClose'> {
    onClose: () => void,
    targetType: DeleteTargetTypes,
    targetId: number
}

const getTargetTypeString = (targetType: DeleteTargetTypes) => {
    switch(targetType) {
        case DeleteTargetTypes.Stage:
            return "stage";
        case DeleteTargetTypes.Lot:
            return "lot";
        case DeleteTargetTypes.Contract:
            return "contract";
        case DeleteTargetTypes.Attachment:
            return "attachment";
        case DeleteTargetTypes.LotNote:
            return "note";
        case DeleteTargetTypes.Asset:
            return "asset";
        default:
            return "item";
    }
}



const DeleteDialog: FC<IDeleteDialogProps> = ({
    onClose,
    targetId,
    targetType
}) => {
    // const { refresh: refreshProjects } = useContext(ProjectsContext);
    // const { refresh: refreshProject } = useContext(ProjectContext);
    const { refresh: refreshStages } = useContext(StagesContext);
    //const { refresh: refreshStage } = useContext(StageContext);
    const { refresh: refreshLots } = useContext(LotsContext);
    //const { refresh: refreshLot } = useContext(LotContext);
    // const { refresh: refreshProjectSummaries } = useContext(ProjectSummariesContext);
    const { refresh: refreshStageDetails } = useContext(StageDetailsContext);
    const { refresh: refreshStageSummaries } = useContext(StageSummariesContext);
    const { refresh: refreshStageAttachments } = useContext(StageAttachmentsContext);
    const { refresh: refreshLotDetails } = useContext(LotDetailsContext);
    const { refresh: refreshLotSummaries } = useContext(LotSummariesContext);
    const { refresh: refreshLotAttachments } = useContext(LotAttachmentsContext);
    const { refresh: refreshLotNotes } = useContext(LotNotesContext);
    const { refresh: refreshReports } = useContext(ReportsContext);
    const { refresh: refreshReportTemplates } = useContext(ReportTemplatesContext);
    const { refresh: refreshReportPreview } = useContext(ReportPreviewContext);
    const { refresh: refreshReportTemplatePreview } = useContext(ReportTemplatePreviewContext);
    const deleteTarget = useDelete();

    const theme = useTheme();

    const refreshRelevantContexts = async (targetType: DeleteTargetTypes) => {
        switch(targetType) {
            case DeleteTargetTypes.Stage:
                await refreshStages();
                await refreshStageSummaries();
                break;
            case DeleteTargetTypes.Lot:
                await refreshLots();
                await refreshStageDetails();
                await refreshLotSummaries();
                break;
            case DeleteTargetTypes.Contract:
                await refreshLotDetails();
                break;
            case DeleteTargetTypes.Attachment:
                await refreshStageAttachments();
                await refreshLotAttachments();
                break;
            case DeleteTargetTypes.LotNote:
                await refreshLotNotes();
                break;
            case DeleteTargetTypes.Report:
                await refreshReports();
                break;
            case DeleteTargetTypes.ReportTemplate:
                await refreshReportTemplates();
                break;
            case DeleteTargetTypes.Asset:
                await refreshReportPreview();
                await refreshReportTemplatePreview();
                break;
            default:
                break;
        }
    }

    const handleConfirmDelete = async () => {
        await deleteTarget(targetType, targetId);
        await refreshRelevantContexts(targetType);
        onClose();
    }

    const targetTypeString = getTargetTypeString(targetType);
    
    return (
        <StyledDialog 
            onClose={onClose}
            open={true}
            maxWidth={"xs"}
        >
            <DialogTitle
                sx={{
                    backgroundColor: theme.palette.secondary.light,
                    color: "white"
                }}
            >
                Confirm Delete
            </DialogTitle>
            <FormSection
                sx={{
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <Typography>
                    Are you sure you want to delete this {targetTypeString}?
                </Typography>
            </FormSection>
            <DialogActions
                sx={{
                    gap: theme.spacing(2),
                    justifyContent: "center"
                }}
            >
                <StyledButton
                    variant='contained'
                    onClick={handleConfirmDelete}
                >
                    Confirm
                </StyledButton>
                <StyledButton
                    variant='contained'
                    onClick={onClose}
                >
                    Cancel
                </StyledButton>
            </DialogActions>
        </StyledDialog>
    );
}

export default DeleteDialog;